import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';
import { ROUTES } from './routes';
import { ProtectedRoute } from './ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import { LoadingScreen } from '@/components/feedback/LoadingScreen';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { PageTransition } from '@/components/layout/PageTransition';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/pages/Login/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/Dashboard/DashboardPage'));
const AgentsPage = lazy(() => import('@/pages/Agents/AgentsPage'));
const AgentDetailPage = lazy(() => import('@/pages/Agents/AgentDetailPage'));
const TerminalPage = lazy(() => import('@/pages/Terminal/TerminalPage'));
const SettingsPage = lazy(() => import('@/pages/Settings/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/Settings/ProfilePage'));
const PasskeysPage = lazy(() => import('@/pages/Settings/PasskeysPage'));
const TotpPage = lazy(() => import('@/pages/Settings/TotpPage'));
const PhonePage = lazy(() => import('@/pages/Settings/PhonePage'));
const SessionsPage = lazy(() => import('@/pages/Settings/SessionsPage'));
const PreferencesPage = lazy(() => import('@/pages/Settings/PreferencesPage'));
const RecoveryPage = lazy(() => import('@/pages/Auth/RecoveryPage'));
const RecoveryVerifyPage = lazy(() => import('@/pages/Auth/RecoveryVerifyPage'));

// Suspense wrapper for lazy components with customizable loading message
function LazyPage({ children, message }: { children: React.ReactNode; message?: string }) {
  return (
    <Suspense fallback={<LoadingScreen message={message} />}>
      <PageTransition>
        {children}
      </PageTransition>
    </Suspense>
  );
}

// Router configuration
export const router = createBrowserRouter([
  // Public routes
  {
    path: ROUTES.LOGIN,
    element: (
      <LazyPage message="Loading login...">
        <LoginPage />
      </LazyPage>
    ),
  },
  {
    path: ROUTES.RECOVERY,
    element: (
      <LazyPage message="Loading recovery...">
        <RecoveryPage />
      </LazyPage>
    ),
  },
  {
    path: ROUTES.RECOVERY_VERIFY,
    element: (
      <LazyPage message="Loading verification...">
        <RecoveryVerifyPage />
      </LazyPage>
    ),
  },
  
  // Protected routes with layout
  {
    element: (
      <ProtectedRoute>
        <Layout>
          <Outlet />
        </Layout>
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: ROUTES.DASHBOARD,
        element: (
          <LazyPage message="Loading dashboard...">
            <DashboardPage />
          </LazyPage>
        ),
      },
      {
        path: ROUTES.AGENTS,
        element: (
          <LazyPage message="Loading agents...">
            <AgentsPage />
          </LazyPage>
        ),
      },
      {
        path: ROUTES.AGENT_DETAIL,
        element: (
          <LazyPage message="Loading agent details...">
            <AgentDetailPage />
          </LazyPage>
        ),
      },
      {
        path: ROUTES.AGENT_NEW,
        element: (
          <LazyPage message="Loading agents...">
            <AgentsPage />
          </LazyPage>
        ), // Opens registration modal
      },
      {
        path: ROUTES.TERMINAL,
        element: (
          <LazyPage message="Loading terminal...">
            <TerminalPage />
          </LazyPage>
        ),
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <LazyPage message="Loading settings...">
            <SettingsPage />
          </LazyPage>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.SETTINGS_PROFILE} replace />,
          },
          {
            path: 'profile',
            element: (
              <LazyPage message="Loading profile...">
                <ProfilePage />
              </LazyPage>
            ),
          },
          {
            path: 'passkeys',
            element: (
              <LazyPage message="Loading passkeys...">
                <PasskeysPage />
              </LazyPage>
            ),
          },
          {
            path: 'totp',
            element: (
              <LazyPage message="Loading authenticator settings...">
                <TotpPage />
              </LazyPage>
            ),
          },
          {
            path: 'phone',
            element: (
              <LazyPage message="Loading phone settings...">
                <PhonePage />
              </LazyPage>
            ),
          },
          {
            path: 'sessions',
            element: (
              <LazyPage message="Loading sessions...">
                <SessionsPage />
              </LazyPage>
            ),
          },
          {
            path: 'preferences',
            element: (
              <LazyPage message="Loading preferences...">
                <PreferencesPage />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },
  
  // Catch-all redirect to dashboard
  {
    path: '*',
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
]);

// Router provider component
export function AppRouter() {
  return <RouterProvider router={router} />;
}
